// ==================== GURUKUL — DOCUMENTS (admin) ====================
// The register of every stored document: what it is, where it is stored (R2 or the VPS Vault),
// every version, checksum and size. Upload goes straight to R2 through a short-lived signed link.
import { showToast, showConfirm, showPrompt } from '../components/toasts.js';

function esc(text) {
  return String(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}
const post = body => api('/api/gurukul/documents', { method: 'POST', body: JSON.stringify(body) });

let categories = [];
let vaultConnected = false;
let page = 1;
let openId = null;
const fmtSize = n => n == null ? '—' : n < 1024 ? n + ' B' : n < 1048576 ? (n / 1024).toFixed(1) + ' KB' : (n / 1048576).toFixed(1) + ' MB';
const fmtDate = d => d ? new Date(d).toLocaleString() : '—';

export function getGurukulDocumentsPage() {
  return `
    <h1 class="page-title">Documents</h1>
    <div class="card">
      <h2>Upload a document</h2>
      <p class="page-intro">Files are stored in Gurukul's own private R2 bucket and registered here with a document number and version history. Uploading again to the same document creates a new version; nothing is overwritten.</p>
      <form id="gkDocForm" class="form-stack" autocomplete="off">
        <div class="form-grid-row">
          <div class="form-group"><label for="gkDocCat">Category</label><select id="gkDocCat" style="max-width:300px;width:100%;"></select></div>
          <div class="form-group"><label for="gkDocTitle">Title</label><input type="text" id="gkDocTitle" maxlength="200" required style="max-width:300px;width:100%;"></div>
        </div>
        <div class="form-group"><label for="gkDocFile">File</label><input type="file" id="gkDocFile" required></div>
        <p id="gkDocHint" class="page-intro"></p>
        <button type="submit" id="gkDocBtn">Upload</button>
      </form>
    </div>

    <div class="card">
      <h2>Register</h2>
      <div class="gk-filter-row" style="display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;margin-bottom:12px;">
        <div class="form-group" style="flex:1;min-width:200px;margin:0;"><label for="gkDocQ">Search</label><input type="text" id="gkDocQ" maxlength="80" placeholder="Title or GD-number"></div>
        <div class="form-group" style="margin:0;"><label for="gkDocFCat">Category</label><select id="gkDocFCat"><option value="">All</option></select></div>
        <div class="form-group" style="margin:0;"><label for="gkDocFTier">Stored in</label><select id="gkDocFTier"><option value="">Anywhere</option><option value="r2">R2</option><option value="vault">Vault (VPS)</option></select></div>
        <div class="form-group" style="margin:0;"><label for="gkDocFStatus">Status</label><select id="gkDocFStatus"><option value="active">Active</option><option value="archived">Archived</option><option value="deleted">Deleted</option></select></div>
        <button type="button" id="gkDocGo">Search</button>
      </div>
      <div class="table-container"><table class="table-stack-mobile">
        <thead><tr><th>Doc</th><th>Title</th><th>Category</th><th>Stored in</th><th>Version</th><th>Size</th><th>Updated</th></tr></thead>
        <tbody id="gkDocBody"><tr><td colspan="7" class="empty-state">Loading…</td></tr></tbody>
      </table></div>
      <div style="display:flex;gap:10px;align-items:center;justify-content:flex-end;margin-top:10px;"><span id="gkDocInfo"></span><button type="button" class="secondary" id="gkDocPrev">Prev</button><button type="button" class="secondary" id="gkDocNext">Next</button></div>
    </div>

    <div class="card">
      <h2>Storage report</h2>
      <div id="gkDocReport"><p class="empty-state">Loading…</p></div>
      <button type="button" id="gkDocReconcile" class="secondary" style="margin-top:8px;">Check storage against the register</button>
      <p id="gkDocReconcileOut" class="page-intro"></p>
    </div>

    <div id="gkDocModal" class="modal"><div class="modal-content" style="max-width:860px;">
      <button type="button" class="close-modal" id="gkDocClose">&times;</button>
      <h2 id="gkDocModalTitle">Document</h2><div id="gkDocModalBody"></div>
    </div></div>`;
}

async function loadList() {
  const body = document.getElementById('gkDocBody');
  const qs = new URLSearchParams({
    q: document.getElementById('gkDocQ').value.trim(), category: document.getElementById('gkDocFCat').value,
    tier: document.getElementById('gkDocFTier').value, status: document.getElementById('gkDocFStatus').value, page: String(page),
  });
  try {
    const d = await api('/api/gurukul/documents?' + qs.toString());
    page = d.page;
    document.getElementById('gkDocInfo').textContent = d.total ? `${(d.page - 1) * d.size + 1}–${Math.min(d.page * d.size, d.total)} of ${d.total}` : 'No documents';
    document.getElementById('gkDocPrev').disabled = d.page <= 1;
    document.getElementById('gkDocNext').disabled = d.page >= d.pages;
    body.innerHTML = d.documents.length ? d.documents.map(x => `
      <tr class="row-clickable" data-id="${x.id}">
        <td data-label="Doc"><strong>${esc(x.doc_no)}</strong>${x.legal_hold ? ' <span class="badge badge-danger">hold</span>' : ''}</td>
        <td data-label="Title">${esc(x.title)}</td><td data-label="Category">${esc(x.category_label)}</td>
        <td data-label="Stored in">${x.storage_tier === 'vault' ? 'Vault (VPS)' : 'R2'}</td><td data-label="Version">v${x.current_version || '—'}</td>
        <td data-label="Size">${fmtSize(x.size_bytes == null ? null : Number(x.size_bytes))}</td><td data-label="Updated">${esc(new Date(x.updated_at).toLocaleDateString())}</td>
      </tr>`).join('') : '<tr><td colspan="7" class="empty-state">No documents match.</td></tr>';
    body.querySelectorAll('tr[data-id]').forEach(tr => tr.addEventListener('click', () => openDoc(tr.dataset.id)));
  } catch (err) { body.innerHTML = `<tr><td colspan="7" class="empty-state">Failed to load: ${esc(err.message)}</td></tr>`; }
}

async function loadReport() {
  const box = document.getElementById('gkDocReport');
  try {
    const r = await api('/api/gurukul/documents?report=1');
    box.innerHTML = r.by_category.length ? `<div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Category</th><th>Stored in</th><th>Documents</th><th>Versions</th><th>Size</th></tr></thead><tbody>
      ${r.by_category.map(x => `<tr><td data-label="Category">${esc(x.label)}</td><td data-label="Stored in">${x.storage_tier === 'vault' ? 'Vault (VPS)' : 'R2'}</td><td data-label="Documents">${x.documents}</td><td data-label="Versions">${x.versions}</td><td data-label="Size">${fmtSize(x.bytes)}</td></tr>`).join('')}
      </tbody></table></div>${r.not_stored.length ? `<p class="page-intro">Not stored: ${r.not_stored.map(n => esc(n.upload_status) + ' ' + n.n).join(', ')}</p>` : ''}` : '<p class="page-intro">Nothing stored yet.</p>';
  } catch (err) { box.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; }
}

async function openDoc(id) {
  openId = id;
  const modal = document.getElementById('gkDocModal');
  const body = document.getElementById('gkDocModalBody');
  modal.classList.add('active');
  body.innerHTML = '<p class="empty-state">Loading…</p>';
  let d;
  try { d = await api('/api/gurukul/documents?id=' + encodeURIComponent(id)); } catch (err) { body.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; return; }
  const doc = d.document;
  document.getElementById('gkDocModalTitle').textContent = `${doc.doc_no} — ${doc.title}`;
  body.innerHTML = `
    <div class="table-container"><table class="table-stack-mobile"><tbody>
      <tr><th style="width:30%;text-align:left;">Category</th><td>${esc(doc.category_label)}</td></tr>
      <tr><th style="text-align:left;">Belongs to</th><td>${esc(doc.owner_type)}${doc.owner_id ? ' · ' + esc(doc.owner_id) : ''}</td></tr>
      <tr><th style="text-align:left;">Stored in</th><td>${doc.storage_tier === 'vault' ? 'Vault (VPS disk)' : 'Cloudflare R2 — Gurukul bucket'}</td></tr>
      <tr><th style="text-align:left;">Status</th><td>${esc(doc.status)}${doc.legal_hold ? ' · <strong>legal hold</strong>' : ''}${doc.retention_until ? ' · keep until ' + esc(String(doc.retention_until).slice(0, 10)) : ''}</td></tr>
    </tbody></table></div>
    <h3 style="margin-top:14px;">Versions</h3>
    <div class="table-container"><table class="table-stack-mobile"><thead><tr><th>Version</th><th>File</th><th>Size</th><th>Checksum (SHA-256)</th><th>Uploaded</th><th></th></tr></thead><tbody>
      ${d.versions.map(v => `<tr>
        <td data-label="Version">v${v.version_no}${v.is_current ? ' <span class="badge badge-success">current</span>' : ''}${v.upload_status !== 'stored' ? ` <span class="badge badge-danger">${esc(v.upload_status)}</span>` : ''}</td>
        <td data-label="File">${esc(v.original_filename)}${v.note ? '<br><em>' + esc(v.note) + '</em>' : ''}</td><td data-label="Size">${fmtSize(v.size_bytes)}</td>
        <td data-label="Checksum"><code>${esc((v.sha256 || '').slice(0, 16))}${v.sha256 ? '…' : ''}</code></td>
        <td data-label="Uploaded">${esc(v.uploaded_by_name || '')}<br>${esc(fmtDate(v.uploaded_at))}</td>
        <td data-label="" class="action-buttons">${v.upload_status === 'stored' ? `<button type="button" class="gk-dl" data-v="${v.version_no}">Download</button>` : ''}${v.upload_status === 'stored' && !v.is_current ? ` <button type="button" class="secondary gk-cur" data-v="${v.version_no}">Make current</button>` : ''}</td>
      </tr>`).join('')}
    </tbody></table></div>
    ${d.can_upload_version && doc.status !== 'deleted' ? `<h3 style="margin-top:14px;">Upload a new version</h3>
      <div class="form-group"><input type="file" id="gkDocNewFile"></div>
      <div class="form-group"><input type="text" id="gkDocNewNote" maxlength="200" placeholder="What changed? (optional)" style="max-width:420px;width:100%;"></div>
      <button type="button" id="gkDocNewBtn">Upload new version</button>` : ''}
    <div class="btn-group" style="margin-top:16px;display:flex;gap:8px;flex-wrap:wrap;">
      ${doc.status === 'active' ? '<button type="button" class="secondary" id="gkDocArchive">Archive</button>' : ''}
      ${doc.status === 'archived' ? '<button type="button" class="secondary" id="gkDocUnarchive">Make active</button>' : ''}
      <button type="button" class="secondary" id="gkDocHold">${doc.legal_hold ? 'Remove legal hold' : 'Put on legal hold'}</button>
      ${doc.status !== 'deleted' ? '<button type="button" class="danger" id="gkDocDelete">Delete…</button>' : ''}
    </div>`;

  body.querySelectorAll('.gk-dl').forEach(b => b.addEventListener('click', async () => {
    try { const r = await api(`/api/gurukul/documents?download=${id}&version=${b.dataset.v}`); window.open(r.url, '_blank', 'noopener'); } catch (e) { showToast(e.message, 'error'); }
  }));
  body.querySelectorAll('.gk-cur').forEach(b => b.addEventListener('click', async () => {
    if (!(await showConfirm(`Make version ${b.dataset.v} the current version? Nothing is deleted.`))) return;
    try { await post({ action: 'restore', document_id: id, version_no: Number(b.dataset.v) }); showToast('Version restored', 'success'); await openDoc(id); loadList(); } catch (e) { showToast(e.message, 'error'); }
  }));
  document.getElementById('gkDocNewBtn')?.addEventListener('click', async () => {
    const f = document.getElementById('gkDocNewFile').files[0];
    if (!f) { showToast('Choose a file', 'error'); return; }
    try {
      await uploadFile(f, { document_id: id, category: doc.category_code, note: document.getElementById('gkDocNewNote').value.trim() });
      showToast('New version stored', 'success'); await openDoc(id); loadList(); loadReport();
    } catch (e) { showToast(e.message, 'error'); }
  });
  const setStatus = async (status, reason) => { await post({ action: 'status', document_id: id, status, reason }); showToast('Updated', 'success'); await openDoc(id); loadList(); };
  document.getElementById('gkDocArchive')?.addEventListener('click', async () => { if (await showConfirm('Archive this document? It stays stored and can be made active again.')) try { await setStatus('archived'); } catch (e) { showToast(e.message, 'error'); } });
  document.getElementById('gkDocUnarchive')?.addEventListener('click', async () => { try { await setStatus('active'); } catch (e) { showToast(e.message, 'error'); } });
  document.getElementById('gkDocDelete')?.addEventListener('click', async () => {
    const reason = await showPrompt('Reason for deleting (required)', { title: 'Delete document', okLabel: 'Delete' });
    if (!reason) return;
    if (!(await showConfirm(`Delete ${esc(doc.doc_no)}? It will be marked deleted; a legal hold or retention period blocks this.`))) return;
    try { await setStatus('deleted', reason); } catch (e) { showToast(e.message, 'error'); }
  });
  document.getElementById('gkDocHold')?.addEventListener('click', async () => {
    const on = !doc.legal_hold;
    if (!(await showConfirm(on ? 'Put this document on legal hold? It cannot be deleted until the hold is removed.' : 'Remove the legal hold?'))) return;
    try { await post({ action: 'hold', document_id: id, hold: on, retention_until: doc.retention_until ? String(doc.retention_until).slice(0, 10) : null }); showToast('Updated', 'success'); await openDoc(id); loadList(); } catch (e) { showToast(e.message, 'error'); }
  });
}

/** init → upload straight to R2 with the signed link → confirm (server checks size and records the checksum). */
async function uploadFile(file, meta) {
  const init = await post({ action: 'init_upload', filename: file.name, mime_type: file.type || 'application/octet-stream', size_bytes: file.size, owner_type: 'system', ...meta });
  const put = await fetch(init.upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': init.content_type } });
  if (!put.ok) throw new Error('Upload to storage failed (' + put.status + ')');
  return post({ action: 'confirm', version_id: init.version_id });
}

export async function initGurukulDocumentsPage() {
  try {
    const c = await api('/api/gurukul/documents?categories=1');
    categories = c.categories; vaultConnected = c.vault_connected;
  } catch (err) { showToast(err.message, 'error'); }
  const uploadable = categories.filter(x => x.default_tier === 'r2');
  document.getElementById('gkDocCat').innerHTML = uploadable.map(x => `<option value="${esc(x.code)}">${esc(x.label)}</option>`).join('');
  document.getElementById('gkDocFCat').innerHTML = '<option value="">All</option>' + categories.map(x => `<option value="${esc(x.code)}">${esc(x.label)}</option>`).join('');
  const hint = () => {
    const c = uploadable.find(x => x.code === document.getElementById('gkDocCat').value);
    document.getElementById('gkDocHint').textContent = c ? `${c.description || ''} Allowed: ${c.allowed_mime.map(m => m.split('/')[1]).join(', ')} · up to ${c.max_size_mb} MB.` : '';
  };
  document.getElementById('gkDocCat').addEventListener('change', hint); hint();

  document.getElementById('gkDocForm').addEventListener('submit', async e => {
    e.preventDefault();
    const f = document.getElementById('gkDocFile').files[0];
    const btn = document.getElementById('gkDocBtn');
    btn.disabled = true; btn.textContent = 'Uploading…';
    try {
      const r = await uploadFile(f, { category: document.getElementById('gkDocCat').value, title: document.getElementById('gkDocTitle').value.trim() });
      showToast(`Stored as ${r.doc_no}`, 'success');
      e.target.reset(); hint(); page = 1; loadList(); loadReport();
    } catch (err) { showToast(err.message, 'error'); } finally { btn.disabled = false; btn.textContent = 'Upload'; }
  });
  const reload = () => { page = 1; loadList(); };
  document.getElementById('gkDocGo').addEventListener('click', reload);
  document.getElementById('gkDocQ').addEventListener('keydown', e => { if (e.key === 'Enter') reload(); });
  ['gkDocFCat', 'gkDocFTier', 'gkDocFStatus'].forEach(id => document.getElementById(id).addEventListener('change', reload));
  document.getElementById('gkDocPrev').addEventListener('click', () => { page = Math.max(page - 1, 1); loadList(); });
  document.getElementById('gkDocNext').addEventListener('click', () => { page += 1; loadList(); });
  document.getElementById('gkDocClose').addEventListener('click', () => document.getElementById('gkDocModal').classList.remove('active'));
  document.getElementById('gkDocReconcile').addEventListener('click', async () => {
    const out = document.getElementById('gkDocReconcileOut');
    out.textContent = 'Checking…';
    try {
      const r = await post({ action: 'reconcile' });
      out.textContent = r.problems.length ? `Checked ${r.checked} file(s): ${r.problems.length} problem(s) — ` + r.problems.map(p => `${p.doc_no} v${p.version_no}: ${p.problem}`).join('; ') : `Checked ${r.checked} file(s): everything matches the register.`;
      loadReport();
    } catch (e) { out.textContent = e.message; }
  });
  loadList(); loadReport();
}
